import React from 'react';
import { X, Pencil, Trash2, Clock, Calendar } from 'lucide-react';
import { CalendarEvent } from '../../../hooks/useSupabaseEvents';
import { resolveCalendar } from '../../../utils/calendar/eventHelpers';
import { Calendar as CalendarType } from '../../../types';
import { useSwipeDismiss } from '../../../hooks/useSwipeDismiss';
import { useDateFormat, formatShortDate } from '../../settings/TimePicker';

interface MobileEventSheetProps {
  event: CalendarEvent | null;
  calendars: CalendarType[];
  onEdit: (event: CalendarEvent) => void;
  onDelete: (event: CalendarEvent) => void;
  onClose: () => void;
}

export const MobileEventSheet: React.FC<MobileEventSheetProps> = ({
  event,
  calendars,
  onEdit,
  onDelete,
  onClose,
}) => {
  const { sheetRef, onTouchStart, onTouchMove, onTouchEnd } = useSwipeDismiss(onClose);
  const dateFormat = useDateFormat();

  if (!event) return null;

  const tag = resolveCalendar(event, calendars);
  const startDate = event.dates[0] || null;
  const endDate = event.dates.length > 1 ? event.dates[event.dates.length - 1] : null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/30"
        onClick={onClose}
      />

      {/* Sheet */}
      <div ref={sheetRef} className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--surface-elevated)] rounded-t-2xl animate-slideUp pb-[env(safe-area-inset-bottom)]"
        style={{ boxShadow: '0 -4px 20px rgba(0,0,0,0.15)', maxHeight: '70vh' }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-2 pb-1 cursor-grab"
          onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        >
          <div className="w-8 h-1 rounded-full bg-[var(--content-tertiary)] opacity-40" />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between px-4 pb-3">
          <div className="flex-1 min-w-0">
            {tag && (
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className={`w-2.5 h-2.5 rounded-full ${tag.color}`} />
                <span className="text-xs text-[var(--content-secondary)]">{tag.name}</span>
              </div>
            )}
            <h3 className="text-lg font-semibold text-[var(--content-primary)] leading-tight">{event.title}</h3>
          </div>
          <button
            onClick={onClose}
            className="ml-2 p-1.5 rounded-lg text-[var(--content-tertiary)] hover:bg-[var(--sidebar-hover)]"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Details */}
        <div className="px-4 pb-4 space-y-3">
          {/* Date */}
          <div className="flex items-center gap-2 text-sm text-[var(--content-secondary)]">
            <Calendar className="w-4 h-4 text-[var(--content-tertiary)]" />
            <span>
              {startDate && (() => { const d = new Date(startDate + 'T00:00:00'); return `${d.toLocaleDateString('en-US', { weekday: 'short' })}, ${formatShortDate(startDate, dateFormat)}`; })()}
              {endDate && ` – ${(() => { const d = new Date(endDate + 'T00:00:00'); return `${d.toLocaleDateString('en-US', { weekday: 'short' })}, ${formatShortDate(endDate, dateFormat)}`; })()}`}
              {event.dates.length > 1 && ` (${event.dates.length} days)`}
            </span>
          </div>

          {/* Time */}
          {!event.all_day && event.start_time && (
            <div className="flex items-center gap-2 text-sm text-[var(--content-secondary)]">
              <Clock className="w-4 h-4 text-[var(--content-tertiary)]" />
              <span>{event.start_time} – {event.end_time || '—'}</span>
            </div>
          )}

          {/* Notes */}
          {event.notes && (
            <div className="text-sm text-[var(--content-secondary)] bg-[var(--surface-tertiary)] rounded-lg p-3 max-h-24 overflow-y-auto">
              {event.notes}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-4 pb-4 flex gap-2">
          <button
            onClick={() => { onEdit(event); onClose(); }}
            className="flex-1 h-10 flex items-center justify-center gap-2 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: 'var(--surface-tertiary)', color: 'var(--content-primary)' }}
          >
            <Pencil className="w-3.5 h-3.5" />
            Edit
          </button>
          <button
            onClick={() => { onDelete(event); onClose(); }}
            className="h-10 w-10 flex items-center justify-center rounded-lg text-[var(--error)] transition-colors"
            style={{ backgroundColor: 'var(--status-error-bg)' }}
            aria-label="Delete event"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );
};
